import { useEffect, useState } from "react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { SuggestionModal } from "../components/SuggestionModal";
import { ProtonWidget } from "../components/ProtonWidget";
import { useRadiosLegacyScripts } from "../hooks/useLegacyScripts";
import { readPageData, slugFromTitle, syncHeadMeta } from "../lib/bootConfig";
import { RADIOS_ORIGIN } from "../lib/origins";

type SeoRadioGroup = { title: string; radios: { href: string; title: string }[] };
type LandingCategory = { title?: string; radios?: { id?: number; title?: string }[] };

const SEO_CATEGORIES = 4;
const SEO_PER_CATEGORY = 8;

/** Mesma regra do servidor (pages/home.py): 4 categorias × 8 rádios, sem repetir rádio. */
function groupsFromCategories(categories: LandingCategory[]): SeoRadioGroup[] {
  const seen = new Set<number>();
  return categories
    .filter((cat) => Array.isArray(cat.radios) && cat.radios.length > 0)
    .slice(0, SEO_CATEGORIES)
    .map((cat) => ({
      title: cat.title || "Rádios",
      radios: (cat.radios || [])
        .filter((r) => r.id && r.title && !seen.has(r.id) && seen.add(r.id))
        .map((r) => {
          const slug = slugFromTitle(r.title || "");
          return { title: r.title || "", href: slug ? `/radio/${r.id}-${slug}` : `/radio/${r.id}` };
        }),
    }))
    .filter((group) => group.radios.length > 0);
}

/** Links "Rádios em destaque": JSON embutido pelo servidor; na navegação client-side, busca as categorias. */
function useSeoRadios(): SeoRadioGroup[] {
  const [groups, setGroups] = useState<SeoRadioGroup[]>(() => {
    const data = readPageData<{ page?: string; seoRadios?: SeoRadioGroup[] }>();
    return data?.page === "home" && Array.isArray(data.seoRadios) ? data.seoRadios : [];
  });
  useEffect(() => {
    if (groups.length > 0) return;
    let cancelled = false;
    fetch(`/ajax/radio-search.php?action=get-landing-categories&limit=${SEO_PER_CATEGORY}`)
      .then((r) => r.json())
      .then((d) => {
        const cats = Array.isArray(d?.categories) ? d.categories.filter((c: { key?: string }) => c.key) : [];
        if (!cancelled) setGroups(groupsFromCategories(cats));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [groups.length]);
  return groups;
}

export function HomePage() {
  useRadiosLegacyScripts(true);

  const seoRadios = useSeoRadios();

  useEffect(() => {
    const title = "Escutar rádio online ao vivo: AM, FM e web | Radios.PRO";
    const description =
      "Escute rádios online ao vivo do Brasil e do mundo. Encontre emissoras AM, FM e web por nome, cidade ou segmento no Radios.PRO.";
    syncHeadMeta({
      title,
      description,
      canonical: RADIOS_ORIGIN + "/",
      robots: null,
      ogTitle: title,
      ogDescription: description,
      ogUrl: RADIOS_ORIGIN + "/",
      ogImage: RADIOS_ORIGIN + "/assets/img/og-image.jpg",
      ogSiteName: "Radios.pro",
    });
    document.body.className = "rp-body rp-home rp-loading";
  }, []);

  return (
    <>
      <div id="rp-splash" className="rp-splash" role="status" aria-label="Carregando Radios.PRO">
        <div className="rp-splash__inner">
          <div className="rp-splash__logo-wrap" aria-hidden="true">
            <span className="rp-splash__ring"></span>
            <img className="rp-splash__logo" src="/assets/img/header/logo.png" alt="" width={88} height={88} />
          </div>
        </div>
      </div>

      <Header />
      <SuggestionModal />

      <main>
        <section className="rp-hero" aria-label="Escutar rádio online ao vivo">
          <div className="rp-hero-orbs" aria-hidden="true">
            <span className="orb orb-1"></span>
            <span className="orb orb-2"></span>
            <span className="orb orb-3"></span>
          </div>
          <div className="rp-globe-canvas" id="rp-globe-canvas" aria-hidden="true">
            <div className="rp-globe-loading" id="rp-globe-loading" role="status" aria-live="polite">
              <span className="rp-globe-spinner" aria-hidden="true"></span>
              <span className="rp-globe-loading-label">Carregando mapa…</span>
            </div>
          </div>
          <div className="rp-hero-content">
            <h1 className="rp-hero-title">Escute rádios online ao vivo</h1>
            <p className="rp-hero-tagline">Para escutar rádio online, busque por emissora, cidade ou segmento musical.</p>

            <div className="rp-hero-primary">
              <section className="figma-search rp-hero-search" aria-label="Pesquisar rádio">
                <div className="figma-search-inner">
                  <div className="figma-search-controls">
                    <div className="search-pill" role="search">
                      <input
                        type="search"
                        id="rp-search-input"
                        placeholder="Pesquisar rádio, estação ou programa"
                        aria-label="Pesquisar rádio"
                        autoComplete="off"
                        autoCapitalize="off"
                        autoCorrect="off"
                        spellCheck={false}
                        inputMode="search"
                        enterKeyHint="search"
                        maxLength={80}
                      />
                      <button className="search-btn" aria-label="Pesquisar">
                        <img src="/assets/img/index/input-radio/lupa.png" alt="Buscar" />
                      </button>
                    </div>
                    <div className="search-filters">
                      <select id="filter-segment" aria-label="Todos os segmentos" defaultValue="">
                        <option value="">Todos os segmentos</option>
                      </select>
                      <input
                        type="text"
                        id="filter-location"
                        placeholder="País ou estado"
                        aria-label="País ou estado"
                        autoComplete="off"
                      />
                    </div>
                  </div>
                </div>
              </section>

              <div className="rp-hero-secondary">
                <p className="rp-globe-user-info" id="rp-globe-user-info" role="status" aria-live="polite" hidden>
                  <span className="rp-globe-user-info-loading" hidden>
                    <span className="rp-globe-spinner" aria-hidden="true"></span>
                  </span>
                  <span className="rp-globe-user-info-text"></span>
                </p>
                <button
                  type="button"
                  className="rp-hero-discover"
                  id="rp-globe-discover"
                  aria-pressed="false"
                  data-label-enter="Explorar no mapa"
                  data-label-exit="Voltar à busca"
                >
                  Explorar no mapa
                </button>
              </div>
            </div>
          </div>
        </section>

        <div id="loading-indicator" className="loading-indicator" style={{ display: "none" }}>
          <div className="spinner"></div>
          <p>Carregando rádios...</p>
        </div>

        <section className="radio-grid-section" id="radio-grid-section">
          <div className="radio-grid" id="dynamic-radio-grid"></div>
          <div className="pagination-controls">
            <button id="load-more-btn" className="load-more-button">
              Carregar mais rádios
            </button>
          </div>
        </section>

        <section className="rp-seo-radios" id="rp-seo-radios" aria-label="Rádios em destaque">
          <h2 className="rp-seo-radios-heading">Rádios em destaque</h2>
          <p className="rp-seo-radios-lead">Ouça estações AM, FM e web selecionadas — links diretos para cada rádio.</p>
          {seoRadios.map((cat) => (
            <div className="rp-seo-radios-group" key={cat.title}>
              <h3 className="rp-seo-radios-group-title">{cat.title}</h3>
              <ul className="rp-seo-radios-list">
                {cat.radios.map((r) => (
                  <li key={r.href}>
                    <a href={r.href}>{r.title}</a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </section>

        <section className="rp-seo-guide" aria-labelledby="rp-seo-guide-title">
          <h2 id="rp-seo-guide-title">Como escutar rádio online</h2>
          <p>
            Pesquise pelo nome da rádio ou filtre pela localização e pelo segmento musical. Abra a página da emissora para
            conferir as informações e transmissões disponíveis.
          </p>
          <ol className="rp-seo-guide-steps">
            <li>Escolha uma emissora nos resultados da busca ou na lista de rádios em destaque.</li>
            <li>Na página da rádio, selecione um stream disponível e pressione o botão de reprodução.</li>
            <li>Se o áudio continuar sem funcionar, relate o problema na página para a equipe revisar a transmissão.</li>
          </ol>
          <p>
            <a className="rp-seo-guide-link" href="/como-escutar-radio-online">
              Leia o guia completo para escutar rádio online
            </a>
          </p>
        </section>
      </main>

      <div className="music-player rp-music-player" aria-label="Player">
        <div className="music-player-content">
          <div className="play-music">
            <button className="play-button" aria-label="botão Play">
              <span className="play-button-art" hidden>
                <img className="play-button-logo" alt="" decoding="async" />
              </span>
              <img className="play-button-icon" src="/assets/img/index/hero/play-button.svg" alt="botão Play" />
            </button>
            <div className="play-music-info">
              <span className="music-label">Tocando agora</span>
              <div className="rp-music-scroll music-title-track">
                <h3 className="music-title">
                  <span className="rp-music-scroll__text music-title-text">Stand By...</span>
                </h3>
              </div>
              <div className="rp-music-scroll music-artist-track">
                <p className="music-artist">
                  <span className="rp-music-scroll__text music-artist-text">Radios.PRO</span>
                </p>
              </div>
            </div>
          </div>
          <div className="rp-music-player-footer">
            <p className="music-station">Clique no play ou em alguma estação...</p>
            <div className="rp-music-player-footer-actions">
              <button
                type="button"
                className="music-radio-favorite rp-music-player-favorite"
                hidden
                aria-label="Adicionar aos favoritos"
                aria-pressed="false"
              >
                <i className="far fa-heart favorite-icon-empty" aria-hidden="true"></i>
                <i className="fas fa-heart favorite-icon-filled" aria-hidden="true"></i>
                <span className="radio-favorite-btn-spinner" aria-hidden="true"></span>
              </button>
              <a className="music-radio-details rp-music-player-details" href="#" hidden aria-label="Ver detalhes">
                <span className="rp-music-player-details-label">Ver detalhes</span>
                <i className="fas fa-arrow-up-right-from-square" aria-hidden="true"></i>
              </a>
            </div>
          </div>
        </div>
      </div>

      <div
        id="radiospro-wa-toast"
        className="radiospro-wa-toast"
        aria-hidden="true"
        role="dialog"
        aria-labelledby="radiospro-wa-toast-title"
        aria-describedby="radiospro-wa-toast-text"
      >
        <div className="radiospro-wa-toast__accent" aria-hidden="true"></div>
        <div className="radiospro-wa-toast__inner">
          <div className="radiospro-wa-toast__icon" aria-hidden="true">
            <i className="fab fa-whatsapp"></i>
          </div>
          <div className="radiospro-wa-toast__body">
            <p className="radiospro-wa-toast__brand">Radios.PRO</p>
            <h2 id="radiospro-wa-toast-title" className="radiospro-wa-toast__title">
              Não perca as novidades!
            </h2>
            <p id="radiospro-wa-toast-text" className="radiospro-wa-toast__text">
              Entre no grupo oficial no WhatsApp e receba lançamentos, dicas e novidades da comunidade Radios.PRO.
            </p>
          </div>
        </div>
        <div className="radiospro-wa-toast__actions">
          <button type="button" className="radiospro-wa-toast__dismiss" id="radiospro-wa-toast-dismiss">
            Agora não
          </button>
          <a
            href="https://chat.whatsapp.com/Fwf55O8S71iFHqX9mBnElY"
            className="radiospro-wa-toast__cta"
            target="_blank"
            rel="noopener noreferrer"
            id="radiospro-wa-toast-cta"
          >
            <i className="fab fa-whatsapp" aria-hidden="true"></i>
            <span>Entrar no grupo</span>
          </a>
        </div>
      </div>

      <Footer />
      <ProtonWidget />
    </>
  );
}
